// settingsPlanPaneUtils.js

// Services
import { PLAN_PRICING_MODEL } from 'services/plans/PlanService';

export function formatPlanPrice(plan) {
    if (!plan) return 'No plan assigned';

    const pricingModel = String(plan.pricingModel || '').trim();
    const seatPrice = Number(plan.seatPrice || 0);

    if (pricingModel === PLAN_PRICING_MODEL.FREE || seatPrice <= 0) {
        return 'Free';
    }

    const hasCents = seatPrice % 100 !== 0;
    const seatPriceText = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        maximumFractionDigits: hasCents ? 2 : 0,
        minimumFractionDigits: hasCents ? 2 : 0,
        style: 'currency',
    }).format(seatPrice / 100);

    return `${seatPriceText} / seat per month`;
}

export function formatBytes(bytes) {
    const numericBytes = Number(bytes || 0);
    if (!Number.isFinite(numericBytes) || numericBytes < 1) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(
        Math.floor(Math.log(numericBytes) / Math.log(1024)),
        units.length - 1
    );
    const value = numericBytes / (1024 ** unitIndex);
    const formattedValue = value >= 10 || unitIndex === 0
        ? value.toFixed(0)
        : value.toFixed(1);

    return `${formattedValue} ${units[unitIndex]}`;
}

export function formatUserLimit(plan) {
    const maxUsers = Number(plan?.maxUsers);
    if (!Number.isFinite(maxUsers) || maxUsers < 1) {
        return 'Unlimited';
    }

    return `${Math.floor(maxUsers)} users`;
}

export function formatApiSummary(plan) {
    if (!plan?.apiEnabled) return 'Not included';

    const monthlyRuns = Number(plan.apiMonthlyRunLimit || 0);
    if (!Number.isFinite(monthlyRuns) || monthlyRuns < 1) {
        return 'Included';
    }

    return `${Math.floor(monthlyRuns).toLocaleString()} runs / month`;
}

export function formatLiveSummary(plan) {
    if (!plan?.liveEnabled) return 'Not included';

    const participantMinutes = Math.max(0, Math.floor(Number(plan.liveParticipantMinutesMonthlyCap || 0)));
    const roomCount = Math.max(1, Math.floor(Number(plan.liveConcurrentRoomsCap || 1)));
    const participantCount = Math.max(0, Math.floor(Number(plan.liveMaxParticipantsPerRoom || 0)));

    return `${participantMinutes.toLocaleString()} mins • ${roomCount} room${roomCount === 1 ? '' : 's'} • ${participantCount} / room`;
}

export function formatRecordingSummary(plan) {
    if (!plan?.liveEnabled || !plan?.liveRecordingEnabled) return 'Not included';

    const recordingMinutes = Math.max(0, Math.floor(Number(plan.liveEgressMinutesMonthlyCap || 0)));
    return `${recordingMinutes.toLocaleString()} mins / month`;
}

export function isSelfServeSelectable(plan) {
    if (!plan) return false;

    const pricingModel = String(plan.pricingModel || '').trim();
    const seatPrice = Number(plan.seatPrice || 0);
    return pricingModel === PLAN_PRICING_MODEL.FREE || seatPrice <= 0;
}

export function getDefaultFreePlan(plans) {
    return (Array.isArray(plans) ? plans : []).find((plan) => isSelfServeSelectable(plan)) || null;
}

export function formatBillingStatus(status) {
    const normalized = String(status || '').trim();
    if (!normalized) return 'Not started';

    return normalized
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

export function formatDateTime(value) {
    if (!value) return '—';

    if (value instanceof Date) {
        return value.toLocaleString();
    }

    if (typeof value?.toDate === 'function') {
        const dateValue = value.toDate();
        if (dateValue instanceof Date) {
            return dateValue.toLocaleString();
        }
    }

    return '—';
}

export function formatMonthLabel(monthKey) {
    const normalized = String(monthKey || '').trim();
    if (!normalized) return 'Current month';

    const match = normalized.match(/^(\d{4})-(\d{2})$/);
    if (!match) return normalized;

    const [, year, month] = match;
    const dateValue = new Date(Date.UTC(Number(year), Number(month) - 1, 1));

    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        timeZone: 'UTC',
        year: 'numeric',
    }).format(dateValue);
}

export function formatWholeCount(value) {
    const parsed = Number(value || 0);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return '0';
    }

    return Math.floor(parsed).toLocaleString();
}

export function formatUsageValue({
    formatter = formatWholeCount,
    limit,
    used,
}) {
    const safeLimit = Number(limit || 0);
    const usedText = formatter(used);

    if (!Number.isFinite(safeLimit) || safeLimit < 1) {
        return `${usedText} used`;
    }

    return `${usedText} / ${formatter(safeLimit)}`;
}

export function secondsToWholeMinutes(value) {
    const parsed = Number(value || 0);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return 0;
    }

    return Math.max(0, Math.floor(parsed / 60));
}